import { useMemo, useState } from 'react';
import { Box, Button, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CheckIcon from '@mui/icons-material/Check';
import type { IInfluencer, IPriceCard } from '../../../redux/buzzbridge/types';
import type { RateCardPlatformId } from '../utils/rateCardPlatformConfig';
import { RateCardBuilderModal } from './RateCardBuilderModal';
import {
    LOYALTY_DISPLAY_FONT,
    LOYALTY_GOLD,
    LOYALTY_ON_VARIANT,
    LOYALTY_SURFACE_HIGHEST,
    loyaltyBodyTextSx,
    loyaltyHeadlineSx,
    loyaltyLabelTextSx,
} from '../Loyalty/loyaltyTheme';
import { buildInfluencerRateCardShareUrl } from '../utils/influencerRateCardShareUrl';
import { copyRateCardLink, openRateCardEmailShare } from '../utils/influencerRateCardShare';

export interface InfluencerRateCardShareEntryProps {
    influencer: IInfluencer;
    onSaveRateCard?: (platform: RateCardPlatformId, pricing: Partial<IPriceCard>) => void;
    savingRateCard?: boolean;
}

/** Share public rate card — same pattern as Referral rewards (link + copy + email). */
export function InfluencerRateCardShareEntry({
    influencer,
    onSaveRateCard,
    savingRateCard,
}: InfluencerRateCardShareEntryProps) {
    const [copied, setCopied] = useState(false);
    const [builderOpen, setBuilderOpen] = useState(false);
    const link = useMemo(() => buildInfluencerRateCardShareUrl(influencer), [influencer]);
    const hidden = Boolean(influencer.hide_rate_card);

    const handleCopy = async () => {
        if (!link) return;
        const ok = await copyRateCardLink(link);
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleEmail = () => {
        if (!link) return;
        openRateCardEmailShare(link, influencer.name);
    };

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '32px',
                bgcolor: '#131313',
                p: { xs: 2.5, md: 3 },
                mb: 0,
                height: '100%',
                transition: 'box-shadow 0.5s',
                '&:hover': { boxShadow: '0 0 40px rgba(254,183,0,0.1)' },
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    top: -48,
                    right: -48,
                    width: 192,
                    height: 192,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,201,101,0.1)',
                    filter: 'blur(48px)',
                    pointerEvents: 'none',
                }}
            />

            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '16px',
                            bgcolor: LOYALTY_SURFACE_HIGHEST,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.35)',
                        }}>
                        <SellOutlinedIcon sx={{ color: LOYALTY_GOLD, fontSize: 28 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ ...loyaltyHeadlineSx, color: '#fff', fontSize: 20, mb: 0.5 }}>
                            Rate card
                        </Typography>
                        <Typography sx={{ ...loyaltyBodyTextSx, maxWidth: 420, lineHeight: 1.5 }}>
                            {hidden
                                ? 'Your rate card is hidden on your public profile. Unhide it in profile settings to share with brands.'
                                : 'Share your public rate card so brands can view your rates and collaborate with you.'}
                        </Typography>
                    </Box>
                </Box>

                {!hidden ? (
                    <Box
                        sx={{
                            bgcolor: 'rgba(0,0,0,0.25)',
                            border: '1px solid rgba(72,72,71,0.35)',
                            borderRadius: '16px',
                            p: { xs: 2, sm: 2.5 },
                        }}>
                        <Typography sx={{ ...loyaltyLabelTextSx, fontSize: 10, mb: 1.5, letterSpacing: '0.2em' }}>
                            Your rate card link
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 1.5,
                                alignItems: { sm: 'center' },
                            }}>
                            <TextField
                                fullWidth
                                size="small"
                                value={link ?? ''}
                                placeholder="Set a username on your profile to generate a link"
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => void handleCopy()}
                                                disabled={!link}
                                                aria-label="Copy rate card link"
                                                sx={{ color: copied ? '#00fdc1' : LOYALTY_GOLD }}>
                                                {copied ? (
                                                    <CheckIcon fontSize="small" />
                                                ) : (
                                                    <ContentCopyIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        fontFamily: LOYALTY_DISPLAY_FONT,
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: '#fff',
                                        bgcolor: LOYALTY_SURFACE_HIGHEST,
                                        borderRadius: '12px',
                                        '& fieldset': { borderColor: 'rgba(72,72,71,0.4)' },
                                    },
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
                                {onSaveRateCard ? (
                                    <Button
                                        onClick={() => setBuilderOpen(true)}
                                        sx={{
                                            fontFamily: LOYALTY_DISPLAY_FONT,
                                            fontWeight: 700,
                                            fontSize: 12,
                                            color: '#392700',
                                            bgcolor: LOYALTY_GOLD,
                                            borderRadius: '999px',
                                            px: 2.5,
                                            whiteSpace: 'nowrap',
                                            '&:hover': { bgcolor: '#ecaa00' },
                                        }}>
                                        Rate Card Builder
                                    </Button>
                                ) : null}
                                <Button
                                    onClick={() => void handleCopy()}
                                    disabled={!link}
                                    startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                                    sx={{
                                        fontFamily: LOYALTY_DISPLAY_FONT,
                                        fontWeight: 700,
                                        fontSize: 12,
                                        color: LOYALTY_GOLD,
                                        border: '1px solid rgba(255,201,101,0.35)',
                                        borderRadius: '999px',
                                        px: 2.5,
                                        whiteSpace: 'nowrap',
                                        '&:hover': { bgcolor: 'rgba(255,201,101,0.08)' },
                                    }}>
                                    Copy
                                </Button>
                                <Button
                                    onClick={handleEmail}
                                    disabled={!link}
                                    startIcon={<EmailOutlinedIcon />}
                                    sx={{
                                        fontFamily: LOYALTY_DISPLAY_FONT,
                                        fontWeight: 700,
                                        fontSize: 12,
                                        color: LOYALTY_ON_VARIANT,
                                        border: '1px solid rgba(72,72,71,0.5)',
                                        borderRadius: '999px',
                                        px: 2.5,
                                        whiteSpace: 'nowrap',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                                    }}>
                                    Email
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                ) : onSaveRateCard ? (
                    <Button
                        onClick={() => setBuilderOpen(true)}
                        sx={{
                            alignSelf: 'flex-start',
                            fontFamily: LOYALTY_DISPLAY_FONT,
                            fontWeight: 700,
                            fontSize: 12,
                            color: '#392700',
                            bgcolor: LOYALTY_GOLD,
                            borderRadius: '999px',
                            px: 2.5,
                            '&:hover': { bgcolor: '#ecaa00' },
                        }}>
                        Rate Card Builder
                    </Button>
                ) : null}
            </Box>
            {onSaveRateCard ? (
                <RateCardBuilderModal
                    open={builderOpen}
                    onClose={() => setBuilderOpen(false)}
                    influencer={influencer}
                    initialPlatform="instagram"
                    saving={savingRateCard}
                    onSave={(platform, pricing) => {
                        onSaveRateCard(platform, pricing);
                        setBuilderOpen(false);
                    }}
                />
            ) : null}
        </Box>
    );
}
